import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { Button, Flex, Input, Select, Tabs, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { InventoryV2, LocationLockEnum, WLocation } from '@/types/inventoryV2';
import SText from '@/styles/SText';
import { useTranslation } from 'react-i18next';
import useLabelColor from './hooks/useLabelColor';
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from '@/styles/STableLayout';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { AnyObject } from 'antd/es/_util/type';
import useDataList from '@/hooks/useDataList';
import { CommonListParams } from '@/types/common';
import { list, realList, locationList, specList, InventoryListParam } from '@/apis/inventoryv2';
import MaterialDetail, { MaterialType } from './components/MaterialDetail';
import LocationDetail from './components/LocationDetail';
import BarcodeList from './components/BarcodeList';
import PrintLocation from '../LocationManage/components/PrintLocation';
// import LocationForm from './components/LocationForm';
import { getLocationCode } from '../../utils';
import { getLocationIdx } from '@/utils/wLocation';
import ExportExcelButton from '@/components/ExportExcelButton';
import ExportProgressModal from '@/components/ExportProgressModal';
import { numFormmater } from '@/pages/Requirement/util';
import { DictContext } from '@/layout/context/DictContext';
import SelectLocationType from '@/components/Selector/SelectLocationType';
import SelectCategory from '@/components/Selector/SelectCategory';
import ListFilterBar from '@/components/ListFilterBar';
import useCommittedFilters from '@/components/ListFilterBar/hooks/useCommittedFilters';
import { hasPermission } from '@/utils/user';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { UserPermissionEnum } from '@/types/user';
import { Product } from '@/types/product';
import ProductInspectConfig from '../Inspection/components/ProductInspectConfig';

enum TabEnum {
  MATERIAL = '1',
  LOCATION = '2',
  SPECIAL = '3',
  REAL = '4', // 真实库列表
}

type SearchFilters = {
  keyword: string;
  materialLocationTypes: number[];
  locationType: number | undefined;
  category: string | undefined;
};

const INITIAL_INVENTORY_FILTERS: SearchFilters = {
  keyword: '',
  materialLocationTypes: [],
  locationType: undefined,
  category: undefined,
};

const InventoryManage: React.FC<{ header: ReactNode }> = ({ header }) => {
  const { t } = useTranslation();
  const { curUser } = useSelector((state: RootState) => state.userReducer)
  const tableRef = useRef<TableLayoutRef | null>(null);
  const { getLabelColor } = useLabelColor();
  const { locationTypes } = useContext(DictContext);
  const [mList, mTotal, mLoading, { fetchList: getMList }] = useDataList<InventoryV2, [CommonListParams<InventoryListParam>]>({ fetchApi: list });
  const [rList, rTotal, rLoading, { fetchList: getRList }] = useDataList<InventoryV2, [CommonListParams<InventoryListParam>]>({ fetchApi: realList });
  // 隐藏新增/修改库位功能时，注释掉addLItem和updateLItem
  const [lList, lTotal, lLoading, { fetchList: getLList }] = useDataList<WLocation, [CommonListParams, number | undefined]>({ fetchApi: locationList });
  const [sList, sTotal, sLoading, { fetchList: getSList }] = useDataList<InventoryV2, [CommonListParams]>({ fetchApi: specList });

  const [activeTab, setActiveTab] = useState<TabEnum>(TabEnum.MATERIAL);
  const [mDetail, setMDetail] = useState<InventoryV2 | null>(null);
  const [materialType, setMaterialType] = useState<MaterialType>("COMMON");
  const [lDetail, setLDetail] = useState<WLocation | null>(null);
  const [barcodeInfo, setBarcodeInfo] = useState<{ id: string; type: 'inventory' | 'location' } | null>(null);
  // 隐藏新增/修改库位功能时，注释掉这些状态
  // const [locationFormOpen, setLocationFormOpen] = useState(false);
  // const [editingLocation, setEditingLocation] = useState<WLocation | undefined>(undefined);
  const [exportProgressVisible, setExportProgressVisible] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [printLoading, setPrintLoading] = useState(false);
  const [configProduct, setConfigProduct] = useState<Product | null>(null);

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<SearchFilters>(INITIAL_INVENTORY_FILTERS);
  const { keyword, materialLocationTypes, locationType, category } = draftFilters;

  // ✅ AbortControllers for each tab
  const mAbortControllerRef = useRef<AbortController | null>(null);
  const lAbortControllerRef = useRef<AbortController | null>(null);
  const sAbortControllerRef = useRef<AbortController | null>(null);
  const rAbortControllerRef = useRef<AbortController | null>(null);

  // const [startTime, setStartTime] = useState<Dayjs | null>();
  // const [endTime, setEndTime] = useState<Dayjs | null>();



  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_INVENTORY_FILTERS);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, replaceFilters])

  // 切换tab时清空选中行
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [activeTab]);

  // ✅ Cancel other tabs' requests when activeTab changes
  useEffect(() => {
    return () => {
      if (activeTab === TabEnum.MATERIAL) {
        lAbortControllerRef.current?.abort();
        sAbortControllerRef.current?.abort();
        rAbortControllerRef.current?.abort();
      } else if (activeTab === TabEnum.LOCATION) {
        mAbortControllerRef.current?.abort();
        sAbortControllerRef.current?.abort();
        rAbortControllerRef.current?.abort();
      } else if (activeTab === TabEnum.SPECIAL) {
        mAbortControllerRef.current?.abort();
        lAbortControllerRef.current?.abort();
        rAbortControllerRef.current?.abort();
      } else if (activeTab === TabEnum.REAL) {
        mAbortControllerRef.current?.abort();
        lAbortControllerRef.current?.abort();
        sAbortControllerRef.current?.abort();
      }
    };
  }, [activeTab]);

  // 切换 tab 时重置 keyword
  const handleTabChange = (key: string) => {
    setActiveTab(key as TabEnum);
  };

  const tableTabs = [
    // {
    //   key: TabEnum.SPECIAL,
    //   label: t('warehouse.specialMaterialList'),
    // },
    {
      key: TabEnum.MATERIAL,
      label: t('warehouse.normalMaterialList'),
    },
    {
      key: TabEnum.LOCATION,
      label: t('warehouse.locationList'),
    },
    {
      key: TabEnum.REAL,
      label: t('warehouse.realMaterialList'),
    },
  ];

  const MaterialColums: ColumnsType<InventoryV2> = [
    {
      title: t("purchase.productInfo"),
      children: [
        {
          title: <SText text={t("product.code")} />,
          dataIndex: ["product", "code"],
          width: 100,
          sorter: (a, b) => {
            const valA = a?.product?.code ?? "";
            const valB = b?.product?.code ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, item) => <SText text={item?.product?.code ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("product.name")} />,
          dataIndex: "name",
          width: 100,
          render: (_, item) => <SText text={item?.product?.name ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("product.brand1")} />,
          dataIndex: "model1",
          width: 120,
          render: (_, item) => <SText text={item?.product?.model ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("product.uom")} />,
          dataIndex: "uom",
          width: 80,
          render: (_, item) => <SText text={item?.product?.uom ?? ''} style={{ width: "100%" }} />,
        },
      ]
    },
    {
      title: t("order.quantity"),
      key: "quantity",
      width: 100,
      dataIndex: "count",
      fixed: "right",
      sorter: (a, b) => (a?.count ?? 0) - (b?.count ?? 0),
      render: (_, record) => numFormmater(record.count || 0),
    },
    {
      title: <SText text={`${t("product.minStockCount")}(${t("product.alert")})`} />,
      key: "minStockCount",
      width: 100,
      fixed: "right",
      render: (_, record) => {
        if (record.standard) {
          let lower = record.count < record.standard.minStockCount;
          if (lower) return (
            <Tooltip title={`${t('product.lowerMinCount', { minStockCount: numFormmater(record.standard.minStockCount) })}`}>
              <div style={{ color: "red" }}>{numFormmater(record.standard.minStockCount || 0)}</div>
            </Tooltip>
          )
          return <span>{numFormmater(record.standard.minStockCount || 0)}</span>
        }
        return null
      },
    },
    {
      title: <SText text={`${t("product.maxStockCount")}(${t("product.alert")})`} />,
      key: "maxStockCount",
      width: 100,
      fixed: "right",
      render: (_, record) => {
        if (record.standard) {
          let over = record.count > record.standard.maxStockCount;
          if (over) return (
            <Tooltip title={`${t('product.overMaxCount', { maxStockCount: numFormmater(record.standard.maxStockCount) })}`}>
              <div style={{ color: "red" }}>{numFormmater(record.standard.maxStockCount || 0)}</div>
            </Tooltip>
          )
          return <span>{numFormmater(record.standard.maxStockCount || 0)}</span>
        }
        return null
      },
    },
    // {
    //   title: <SText text={t("product.alertDetail")} />,
    //   key: "standard",
    //   width: 100,
    //   dataIndex: "standard",
    //   fixed: "right",
    //   render: (_, record) => {
    //     if (record.standard) {
    //       let over = record.count > record.standard.maxStockCount;
    //       let lower = record.count < record.standard.minStockCount;
    //       over && <Tooltip title={`${t('product.overMaxCount', { maxStockCount: record.standard.maxStockCount })}`}>
    //         <SText tagMode text={t('')} />
    //       </Tooltip>
    //       return null;
    //     } else return null
    //   },
    // },
    {
      title: t("warehouse.preIn"),
      key: "preIn",
      width: 100,
      dataIndex: "preIn",
      fixed: "right",
      sorter: (a, b) => (a?.preIn ?? 0) - (b?.preIn ?? 0),
      render: (quantity) => numFormmater(quantity < 0 ? 0 : quantity),
    },
    {
      title: t("warehouse.preOut"),
      key: "preOut",
      width: 100,
      dataIndex: "preOut",
      fixed: "right",
      sorter: (a, b) => (a?.preOut ?? 0) - (b?.preOut ?? 0),
      render: (quantity) => numFormmater(quantity < 0 ? 0 : quantity),
    },
    {
      title: t("warehouse.lotCount"),
      key: "lotCount",
      width: 100,
      dataIndex: "lotCount",
      fixed: "right",
      sorter: (a, b) => (a?.lotCount ?? 0) - (b?.lotCount ?? 0),
      render: (lotCount) => numFormmater(lotCount < 0 ? 0 : lotCount),
    },
    {
      title: t("common.opt"),
      fixed: "right",
      width: 180,
      render: (_, item) => (
        <>
          <Button type="link" onClick={() => { setMDetail(item); setMaterialType("COMMON"); }}>
            {t("common.detail")}
          </Button>
          <Button type="link" onClick={() => setBarcodeInfo({ id: item.productId, type: 'inventory' })}>
            {t("warehouse.viewBarcode")}
          </Button>
          {hasPermission(curUser, [UserPermissionEnum.INSP_CONFIG_PRO]) && <Button type="link" onClick={() => setConfigProduct(item.product)}>{t('inspection.config')}</Button>}
        </>
      )
    },
  ]

  const SpecialColums: ColumnsType<InventoryV2> = [
    {
      title: t('purchase.productInfo'),
      children: [
        {
          title: <SText text={t('product.code')} />,
          key: 'productCode',
          dataIndex: ["product", "code"],
          width: 120,
          sorter: (a, b) => {
            const valA = a?.product?.code ?? "";
            const valB = b?.product?.code ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, record) => <SText text={record.product?.code || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.name')} />,
          key: 'productName',
          width: 150,
          render: (_, record) => <SText text={record.product?.name || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.brand1')} />,
          key: 'productModel',
          width: 120,
          render: (_, record) => <SText text={record.product?.model || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.uom')} />,
          key: 'uom',
          width: 80,
          align: 'center',
          render: (_, record) => <SText text={record.product?.uom || ''} style={{ width: "100%" }} />
        },
      ]
    },
    {
      title: <SText text={t('warehouse.count')} />,
      dataIndex: 'count',
      key: 'count',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a?.count ?? 0) - (b?.count ?? 0),
      render: (text) => <SText text={text?.toString() || '0'} style={{ width: "100%" }} />
    },
    // {
    //   title: <SText text={t('warehouse.preIn')} />,
    //   dataIndex: 'preIn',
    //   key: 'preIn',
    //   width: 100,
    //   align: 'center',
    //   sorter: (a, b) => (a?.preIn ?? 0) - (b?.preIn ?? 0),
    //   render: (text) => <SText text={text?.toString() || '0'} style={{ width: "100%" }} />
    // },
    // {
    //   title: <SText text={t('warehouse.preOut')} />,
    //   dataIndex: 'preOut',
    //   key: 'preOut',
    //   width: 100,
    //   align: 'center',
    //   sorter: (a, b) => (a?.preOut ?? 0) - (b?.preOut ?? 0),
    //   render: (text) => <SText text={text?.toString() || '0'} style={{ width: "100%" }} />
    // },
    {
      title: <SText text={t('warehouse.lotCount')} />,
      dataIndex: 'lotCount',
      key: 'lotCount',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a?.lotCount ?? 0) - (b?.lotCount ?? 0),
      render: (text) => <SText text={text?.toString() || '0'} style={{ width: "100%" }} />
    },
    {
      title: t("common.opt"),
      fixed: "right",
      width: 100,
      render: (_, item) => (
        <>
          <Button type="link" onClick={() => { setMDetail(item); setMaterialType("SPECIAL"); }}>
            {t("common.detail")}
          </Button>
        </>
      )
    },
  ]

  const LocationColums: ColumnsType<WLocation> = [
    {
      title: <SText text={t("warehouse.locationCode")} />,
      dataIndex: "number",
      width: 260,
      sorter: (a, b) => {
        const valA = getLocationCode(a) ?? "";
        const valB = getLocationCode(b) ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, record) => <SText text={getLocationCode(record)} />
    },
    {
      title: t("warehouse.labels"),
      key: "labels",
      width: 420,
      render: (_, record) => (
        <Flex gap={4} wrap="wrap">
          {record.label1 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label1")} text={record.label1} />}
          {record.label2 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label2")} text={record.label2} />}
          {record.label3 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label3")} text={record.label3} />}
          {record.label4 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label4")} text={record.label4} />}
          {record.label5 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label5")} text={record.label5} />}
        </Flex>
      )
    },
    {
      title: t("warehouse.locationIndex"),
      key: "l1",
      width: 220,
      render: (_, record) => (
        <SText text={getLocationIdx(record)} />
      )
    },
    {
      title: t("inventory.locationType"),
      key: "type",
      width: 140,
      render: (_, record) => {
        let info = locationTypes.find(label => label.key === record.type);
        return (
          info && <SText text={info.desc} />
        )
      }
    },
    {
      title: t("common.desc"),
      dataIndex: "desc",
      key: "desc",
      width: 200,
      render: (text) => <SText text={text || ''} />
    },
    {
      title: t('counting.lockState'),
      dataIndex: 'lockState',
      key: 'lockState',
      width: 120,
      render: (lockState: LocationLockEnum) => {
        const isLocked = lockState === LocationLockEnum.LOCKED;
        return (
          <SText tagMode
            tagIcon={isLocked ? <LockOutlined /> : <UnlockOutlined />}
            color={isLocked ? 'red' : 'green'}
            text={isLocked ? t('counting.counting') : t('counting.notCounting')}
          />
        );
      },
    },
    {
      title: t("common.opt"),
      fixed: "right",
      width: 180,
      render: (_, item) => (
        <>
          <Button type="link" onClick={() => setLDetail(item)}>
            {t("common.detail")}
          </Button>
          {/* 隐藏编辑库位按钮 */}
          {/* <Button type="link" onClick={() => {
            setEditingLocation(item);
            setLocationFormOpen(true);
          }}>
            {t("common.edit")}
          </Button> */}
          <Button type="link" onClick={() => setBarcodeInfo({ id: item.id, type: 'location' })}>
            {t("warehouse.viewBarcode")}
          </Button>
        </>
      )
    },
  ]

  const fetch = (offset: number, filters: SearchFilters) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    let params: CommonListParams<InventoryListParam> = {
      pagination: { offset, take },
      body: {},
      // startTime: startTime?.valueOf(),
      // endTime: endTime?.valueOf()
    }
    const codes = filters.keyword.trim().split(/\s+/).filter(code => code !== '')
    if (activeTab === TabEnum.MATERIAL || activeTab === TabEnum.REAL) {
      if (codes.length > 1) {
        params.body!.ids = codes
        if (filters.category) params.body!.category = filters.category;
      } else {
        params.body = { keyword: filters.keyword, category: filters.category };
      }
      if (filters.materialLocationTypes.length > 0 && activeTab === TabEnum.REAL) {
        params.body!.locationTypes = filters.materialLocationTypes;
      }
    } else if (activeTab === TabEnum.SPECIAL) {
      // 如果是专用料列表，且有多个有效编码，则使用strs参数；否则使用body参数
      // 处理多个存货编码：只支持空格分隔
      // 只有当有多个有效编码时才使用strs参数
      if (codes.length > 1) {
        params.strs = codes;
      } else {
        params.body = { keyword: filters.keyword };
      }
    } else {
      params.body = { keyword: filters.keyword };
    }

    if (activeTab === TabEnum.MATERIAL) {
      // ✅ Cancel previous Material request
      if (mAbortControllerRef.current) {
        mAbortControllerRef.current.abort();
      }
      const controller = new AbortController();
      mAbortControllerRef.current = controller;

      getMList({ ...params, controller }).catch((err) => {
        if (err?.name === 'AbortError') {
          return;
        }
      });
    } else if (activeTab === TabEnum.LOCATION) {
      // ✅ Cancel previous Location request
      if (lAbortControllerRef.current) {
        lAbortControllerRef.current.abort();
      }
      const controller = new AbortController();
      lAbortControllerRef.current = controller;

      getLList({ ...params, body: params?.body?.keyword, controller }, filters.locationType).catch((err) => {
        if (err?.name === 'AbortError') {
          return;
        }
      });
    } else if (activeTab === TabEnum.REAL) {
      // ✅ Cancel previous Real request
      if (rAbortControllerRef.current) {
        rAbortControllerRef.current.abort();
      }
      const controller = new AbortController();
      rAbortControllerRef.current = controller;

      getRList({ ...params, controller }).catch((err) => {
        if (err?.name === 'AbortError') {
          return;
        }
      });
    } else {
      // ✅ Cancel previous Special request
      if (sAbortControllerRef.current) {
        sAbortControllerRef.current.abort();
      }
      const controller = new AbortController();
      sAbortControllerRef.current = controller;

      getSList({ ...params, body: params?.body?.keyword, controller }).catch((err) => {
        if (err?.name === 'AbortError') {
          return;
        }
      });
    }
  }

  const handleSearch = () => {
    const nextFilters = commitFilters();
    setSelectedRowKeys([]);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_INVENTORY_FILTERS);
    setSelectedRowKeys([]);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const materialLocationTypeTip = materialLocationTypes
    .map(value => locationTypes.find(item => item.key === value)?.desc)
    .filter(Boolean)
    .join(', ');

  // 常备科导出列配置
  const materialExportColumns = (t: any) => [
    { key: "code", label: t("product.code") },
    { key: "name", label: t("product.name") },
    { key: "model", label: t("product.brand1") },
    { key: "uom", label: t("product.uom") },
    { key: "count", label: t("order.quantity") },
    { key: "preIn", label: t("warehouse.preIn") },
    { key: "preOut", label: t("warehouse.preOut") },
    { key: "lotCount", label: t("warehouse.lotCount") },
  ];

  const materialDefaultSelectedColumns = () => [
    "code", "name", "model", "uom", "count", "preIn", "preOut", "lotCount"
  ];

  const materialExportDataMapper = (item: InventoryV2, t: any) => ({
    [t("product.code")]: item?.product?.code ?? '',
    [t("product.name")]: item?.product?.name ?? '',
    [t("product.brand1")]: item?.product?.model ?? '',
    [t("product.uom")]: item?.product?.uom ?? '',
    [t("order.quantity")]: item?.count ?? 0,
    [t("warehouse.preIn")]: item?.preIn ?? 0,
    [t("warehouse.preOut")]: item?.preOut ?? 0,
    [t("warehouse.lotCount")]: item?.lotCount ?? 0,
  });

  // 库位导出列配置
  const locationExportColumns = (t: any) => [
    { key: "locationCode", label: t("warehouse.locationCode") },
    { key: "labels", label: t("warehouse.labels") },
    { key: "locationIndex", label: t("warehouse.locationIndex") },
    { key: "desc", label: t("common.desc") },
    { key: "lockState", label: t('counting.lockState') },
  ];

  const locationDefaultSelectedColumns = () => [
    "locationCode", "labels", "locationIndex", "desc", "lockState"
  ];

  const locationExportDataMapper = (item: WLocation, t: any) => {
    const labels = [item.label1, item.label2, item.label3, item.label4, item.label5]
      .filter(Boolean)
      .join(', ');
    return {
      [t("warehouse.locationCode")]: getLocationCode(item),
      [t("warehouse.labels")]: labels,
      [t("warehouse.locationIndex")]: getLocationIdx(item),
      [t("common.desc")]: item.desc ?? '',
      [t('counting.lockState')]: item.lockState === LocationLockEnum.LOCKED ? t('counting.counting') : t('counting.notCounting'),
    };
  };

  // 专用科导出列配置
  const specialExportColumns = (t: any) => [
    { key: "code", label: t("product.code") },
    { key: "name", label: t("product.name") },
    { key: "model", label: t("product.brand1") },
    { key: "uom", label: t("product.uom") },
    { key: "count", label: t("warehouse.count") },
    // { key: "preIn", label: t("warehouse.preIn") },
    // { key: "preOut", label: t("warehouse.preOut") },
    { key: "lotCount", label: t("warehouse.lotCount") },
  ];

  const specialDefaultSelectedColumns = () => [
    "code", "name", "model", "uom", "count", "lotCount"
  ];

  const specialExportDataMapper = (item: InventoryV2, t: any) => ({
    [t("product.code")]: item?.product?.code ?? '',
    [t("product.name")]: item?.product?.name ?? '',
    [t("product.brand1")]: item?.product?.model ?? '',
    [t("product.uom")]: item?.product?.uom ?? '',
    [t("warehouse.count")]: item?.count ?? 0,
    // [t("warehouse.preIn")]: item?.preIn ?? 0,
    // [t("warehouse.preOut")]: item?.preOut ?? 0,
    [t("warehouse.lotCount")]: item?.lotCount ?? 0,
  });

  // 获取选中的库位数据
  const getSelectedLocations = (): WLocation[] => {
    return lList.filter(item => selectedRowKeys.includes(item.id));
  };

  // 导出全部数据加载函数
  const handleLoadAllMaterial = async () => {
    setExportProgressVisible(true);
    setExportProgress(0);
    const allData: InventoryV2[] = [];
    const batchSize = 100;
    const totalBatches = Math.ceil(mTotal / batchSize);

    try {
      for (let i = 0; i < totalBatches; i++) {
        const params: CommonListParams<InventoryListParam> = {
          pagination: { offset: i * batchSize, take: batchSize },
          body: {},
        };
        if (appliedFilters.materialLocationTypes.length > 0) {
          params.ints = appliedFilters.materialLocationTypes;
        }
        // 处理多个存货编码：只支持空格分隔
        const codes = appliedFilters.keyword.trim().split(/\s+/).filter(code => code !== '');
        // 只有当有多个有效编码时才使用strs参数
        if (codes.length > 1) {
          params.body!.ids = codes;
        } else {
          params.body = { keyword: appliedFilters.keyword };
        }

        const response = await list(params);
        allData.push(...(response.body ?? []));
        setExportProgress(Math.round(((i + 1) / totalBatches) * 100));
      }
      return allData;
    } finally {
      setExportProgressVisible(false);
      setExportProgress(0);
    }
  };

  const handleLoadAllReal = async () => {
    setExportProgressVisible(true);
    setExportProgress(0);
    const allData: InventoryV2[] = [];
    const batchSize = 100;
    const totalBatches = Math.ceil(rTotal / batchSize);

    try {
      for (let i = 0; i < totalBatches; i++) {
        const params: CommonListParams<InventoryListParam> = {
          pagination: { offset: i * batchSize, take: batchSize },
          body: {}
        };
        if (appliedFilters.materialLocationTypes.length > 0) {
          params.body!.locationTypes = appliedFilters.materialLocationTypes;
        }
        const codes = appliedFilters.keyword.trim().split(/\s+/).filter(code => code !== '');
        if (codes.length > 1) {
          params.body!.ids = codes;
        } else {
          params.body!.keyword = appliedFilters.keyword;
        }

        const response = await realList(params);
        allData.push(...(response.body ?? []));
        setExportProgress(Math.round(((i + 1) / totalBatches) * 100));
      }
      return allData;
    } finally {
      setExportProgressVisible(false);
      setExportProgress(0);
    }
  };

  const handleLoadAllLocation = async () => {
    setExportProgressVisible(true);
    setExportProgress(0);
    const allData: WLocation[] = [];
    const batchSize = 100;
    const totalBatches = Math.ceil(lTotal / batchSize);

    try {
      for (let i = 0; i < totalBatches; i++) {
        const params: CommonListParams = {
          pagination: { offset: i * batchSize, take: batchSize },
          body: appliedFilters.keyword,
        };
        const response = await locationList(params, appliedFilters.locationType);
        allData.push(...(response.body ?? []));
        setExportProgress(Math.round(((i + 1) / totalBatches) * 100));
      }
      return allData;
    } finally {
      setExportProgressVisible(false);
      setExportProgress(0);
    }
  };

  const handleLoadAllSpecial = async () => {
    setExportProgressVisible(true);
    setExportProgress(0);
    const allData: InventoryV2[] = [];
    const batchSize = 100;
    const totalBatches = Math.ceil(sTotal / batchSize);

    try {
      for (let i = 0; i < totalBatches; i++) {
        const params: CommonListParams = {
          pagination: { offset: i * batchSize, take: batchSize },
        };

        // 处理多个存货编码：只支持空格分隔
        const codes = appliedFilters.keyword.trim().split(/\s+/).filter(code => code !== '');
        // 只有当有多个有效编码时才使用strs参数
        if (codes.length > 1) {
          params.strs = codes;
        } else {
          params.body = appliedFilters.keyword;
        }

        const response = await specList(params);
        allData.push(...(response.body ?? []));
        setExportProgress(Math.round(((i + 1) / totalBatches) * 100));
      }
      return allData;
    } finally {
      setExportProgressVisible(false);
      setExportProgress(0);
    }
  };

  const headerChildren = (
    <Flex justify='space-between' style={{ width: "100%" }} align='center'>
      <Flex gap={10} align='center'>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          type="card"
          items={tableTabs}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={activeTab === TabEnum.MATERIAL ? mLoading : activeTab === TabEnum.LOCATION ? lLoading : activeTab === TabEnum.REAL ? rLoading : sLoading}
        >
          {({ submit }) => (
            <>
              {activeTab === TabEnum.REAL &&
                <Tooltip title={materialLocationTypeTip || undefined}>
                  <span style={{ display: 'inline-block' }}>
                    <SelectLocationType
                      mode="multiple"
                      style={{ minWidth: 280 }}
                      placeholder={t('inventory.locationType')}
                      value={materialLocationTypes}
                      onChange={value => updateDraftFilters({ materialLocationTypes: value })}
                      allowClear
                      maxTagCount="responsive"
                    />
                  </span>
                </Tooltip>
              }
              {activeTab === TabEnum.LOCATION &&
                <SelectLocationType style={{ minWidth: 200 }} placeholder={t('inventory.locationType')}
                  value={locationType}
                  onChange={value => updateDraftFilters({ locationType: value })}
                  allowClear
                />
              }
              {(activeTab === TabEnum.MATERIAL || activeTab === TabEnum.REAL) &&
                <SelectCategory
                  style={{ minWidth: 220 }}
                  value={category}
                  onChange={value => updateDraftFilters({ category: value || undefined })}
                />
              }
              <Input
                style={{ width: 250 }}
                placeholder={
                  activeTab === TabEnum.MATERIAL || activeTab === TabEnum.SPECIAL || activeTab === TabEnum.REAL
                    ? t('warehouse.searchMultiCodePlaceholder')
                    : t('warehouse.searchLocationPlaceholder')
                }
                value={keyword}
                onChange={event => updateDraftFilters({ keyword: event.target.value })}
                onPressEnter={submit}
              />
            </>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        headerChildren={headerChildren}
        headerSub={<Flex style={{ marginTop: -10, marginBottom: 10 }} gap={10}>
          {header}
          {activeTab === TabEnum.MATERIAL && (
            <>
              <ExportExcelButton<InventoryV2>
                list={mList}
                columns={materialExportColumns}
                defaultSelectedColumns={materialDefaultSelectedColumns}
                dataMapper={materialExportDataMapper}
                fileName={t('warehouse.normalMaterialList') + '.xlsx'}
                sheetName={t('warehouse.normalMaterialList')}
              />
              <ExportExcelButton<InventoryV2>
                list={mList}
                isAll
                loadData={handleLoadAllMaterial}
                columns={materialExportColumns}
                defaultSelectedColumns={materialDefaultSelectedColumns}
                dataMapper={materialExportDataMapper}
                fileName={t('warehouse.normalMaterialList') + '_all.xlsx'}
                sheetName={t('warehouse.normalMaterialList')}
              />
            </>
          )}
          {/* 隐藏新增库位按钮 */}
          {activeTab === TabEnum.LOCATION && (
            <>
              {/* <Button
                type="primary"
                onClick={() => {
                  setEditingLocation(undefined);
                  setLocationFormOpen(true);
                }}
              >
                {t('warehouse.addLocation')}
              </Button> */}
              <ExportExcelButton<WLocation>
                list={lList}
                columns={locationExportColumns}
                defaultSelectedColumns={locationDefaultSelectedColumns}
                dataMapper={locationExportDataMapper}
                fileName={t('warehouse.locationList') + '.xlsx'}
                sheetName={t('warehouse.locationList')}
              />
              <ExportExcelButton<WLocation>
                list={lList}
                isAll
                loadData={handleLoadAllLocation}
                columns={locationExportColumns}
                defaultSelectedColumns={locationDefaultSelectedColumns}
                dataMapper={locationExportDataMapper}
                fileName={t('warehouse.locationList') + '_all.xlsx'}
                sheetName={t('warehouse.locationList')}
              />
              <PrintLocation
                datas={getSelectedLocations()}
                onStart={() => setPrintLoading(true)}
                onEnd={() => setPrintLoading(false)}
                disabled={selectedRowKeys.length === 0 || printLoading}
              />
            </>
          )}
          {activeTab === TabEnum.SPECIAL && (
            <>
              <ExportExcelButton<InventoryV2>
                list={sList}
                columns={specialExportColumns}
                defaultSelectedColumns={specialDefaultSelectedColumns}
                dataMapper={specialExportDataMapper}
                fileName={t('warehouse.specialMaterialList') + '.xlsx'}
                sheetName={t('warehouse.specialMaterialList')}
              />
              <ExportExcelButton<InventoryV2>
                list={sList}
                isAll
                loadData={handleLoadAllSpecial}
                columns={specialExportColumns}
                defaultSelectedColumns={specialDefaultSelectedColumns}
                dataMapper={specialExportDataMapper}
                fileName={t('warehouse.specialMaterialList') + '_all.xlsx'}
                sheetName={t('warehouse.specialMaterialList')}
              />
            </>
          )}
          {activeTab === TabEnum.REAL && (
            <>
              <ExportExcelButton<InventoryV2>
                list={rList}
                columns={materialExportColumns}
                defaultSelectedColumns={materialDefaultSelectedColumns}
                dataMapper={materialExportDataMapper}
                fileName={t('warehouse.realMaterialList') + '.xlsx'}
                sheetName={t('warehouse.realMaterialList')}
              />
              <ExportExcelButton<InventoryV2>
                list={rList}
                isAll
                loadData={handleLoadAllReal}
                columns={materialExportColumns}
                defaultSelectedColumns={materialDefaultSelectedColumns}
                dataMapper={materialExportDataMapper}
                fileName={t('warehouse.realMaterialList') + '_all.xlsx'}
                sheetName={t('warehouse.realMaterialList')}
              />
            </>
          )}
        </Flex>}
        tableLayoutRef={tableRef}
        total={activeTab === TabEnum.MATERIAL ? mTotal : activeTab === TabEnum.LOCATION ? lTotal : activeTab === TabEnum.REAL ? rTotal : sTotal}
        handleChange={(offset) => { fetch(offset, appliedFilters) }}
        columns={(activeTab === TabEnum.MATERIAL || activeTab === TabEnum.REAL ? MaterialColums : activeTab === TabEnum.LOCATION ? LocationColums : SpecialColums) as ColumnsType<AnyObject>}
        dataSource={activeTab === TabEnum.MATERIAL ? mList : activeTab === TabEnum.LOCATION ? lList : activeTab === TabEnum.REAL ? rList : sList}
        loading={activeTab === TabEnum.MATERIAL ? mLoading : activeTab === TabEnum.LOCATION ? lLoading : activeTab === TabEnum.REAL ? rLoading : sLoading}
        scrollX={activeTab === TabEnum.LOCATION ? 1000 : activeTab === TabEnum.SPECIAL ? 1200 : 1200}
        rowSelection={activeTab === TabEnum.LOCATION ? {
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        } : undefined}
      />
      {mDetail && <MaterialDetail type={materialType} product={mDetail.product} productId={mDetail.productId} onClose={() => setMDetail(null)} />}
      {lDetail && <LocationDetail location={lDetail} onClose={() => setLDetail(null)} />}
      {barcodeInfo && <BarcodeList id={barcodeInfo.id} type={barcodeInfo.type} onClose={() => setBarcodeInfo(null)} />}
      {/* 隐藏新增/修改库位表单 */}
      {/* <LocationForm
        detail={editingLocation}
        open={locationFormOpen}
        onClose={() => {
          setLocationFormOpen(false);
          setEditingLocation(undefined);
        }}
        onDone={(location) => {
          if (editingLocation) {
            updateLItem(location)
          } else {
            addLItem(location)
          }
          setLocationFormOpen(false);
          setEditingLocation(undefined);
        }}
      /> */}
      {configProduct && <ProductInspectConfig product={configProduct} open={!!configProduct} onClose={() => setConfigProduct(null)} />}
      <ExportProgressModal
        visible={exportProgressVisible}
        progress={exportProgress}
      />
    </Flex>
  );
};

export default InventoryManage;
